import yaml from "js-yaml";

export interface ParsedDoc {
  /** Parsed YAML front matter as an object (empty object if none). */
  data: Record<string, any>;
  /** Everything after the closing `---`. */
  body: string;
  /** True if the original file actually had a front-matter block. */
  hadFrontMatter: boolean;
}

const FM_RE = /^﻿?---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/** Split a markdown string into YAML front matter + body. */
export function parse(raw: string): ParsedDoc {
  const m = raw.match(FM_RE);
  if (!m) return { data: {}, body: raw, hadFrontMatter: false };
  let data: Record<string, any> = {};
  try {
    const loaded = yaml.load(m[1]);
    if (loaded && typeof loaded === "object") data = loaded as Record<string, any>;
  } catch {
    // Malformed YAML — treat as no front matter so we never lose the body.
    return { data: {}, body: raw, hadFrontMatter: false };
  }
  return { data, body: raw.slice(m[0].length), hadFrontMatter: true };
}

/** Re-assemble a markdown file from front matter + body. */
export function stringify(data: Record<string, any>, body: string): string {
  const keys = Object.keys(data);
  if (keys.length === 0) return body;
  const yml = yaml
    .dump(data, { lineWidth: -1, flowLevel: 1, noRefs: true, quotingType: '"' })
    .replace(/\n$/, "");
  return `---\n${yml}\n---\n\n${body.replace(/^\n+/, "")}`;
}

export type Status = "published" | "draft" | "unlisted" | "archived";

/** Derive a publication status from front-matter fields. */
export function statusOf(data: Record<string, any>): Status {
  if (data.archived === true) return "archived";
  if (data.draft === true) return "draft";
  if (data.unlisted === true) return "unlisted";
  return "published";
}

/**
 * Apply a status by mutating the draft/unlisted/archived flags in a copy of `data`.
 * 封存 = archived:true + draft:true（讓 Docusaurus build 排除，卻與一般草稿區分）。
 */
export function withStatus(data: Record<string, any>, status: Status): Record<string, any> {
  const next = { ...data };
  delete next.draft;
  delete next.unlisted;
  delete next.archived;
  if (status === "draft") next.draft = true;
  else if (status === "unlisted") next.unlisted = true;
  else if (status === "archived") {
    next.archived = true;
    next.draft = true;
  }
  return next;
}
